import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShopping } from '../contexts/ShoppingContext';
import { useCheckout } from '../contexts/CheckoutContext';
import { useUser } from '../contexts/UserContext';
import { CheckoutFormData, AccountingCode, ShippingRate, ShippingLocation, SpecialDeliveryFormData, ShippingRatesDiagnosticData, AddressCandidate, ECheckFormData } from '../types';
import { PromoCodeSection } from './checkout/PromoCodeSection';
import { AccountingCodeSection } from './checkout/AccountingCodeSection';
import { ShippingAddressSection } from './checkout/ShippingAddressSection';
import { ShippingMethodSection } from './checkout/ShippingMethodSection';
import { SpecialDeliverySection } from './checkout/SpecialDeliverySection';
import { PaymentMethodSection } from './checkout/PaymentMethodSection';
import { CreditCardSection } from './checkout/CreditCardSection';
import { ECheckSection } from './checkout/ECheckSection';
import { OrderSummary } from './checkout/OrderSummary';
import { GuestEmailSection } from './checkout/GuestEmailSection';
import { CommentsSection } from './checkout/CommentsSection';
import { OrderInformationSection } from './checkout/OrderInformationSection';
import { InitialsAcknowledgmentSection } from './checkout/InitialsAcknowledgmentSection';
import { DiagnosticPanel } from './checkout/DiagnosticPanel';
import { AddressCandidatesModal } from './checkout/AddressCandidatesModal';
import { useGuestMode } from '../hooks/useGuestMode';
import { useClientDataContext } from '../contexts/ClientDataContext';
import { ShippingService } from '../services/shippingService';
import { CheckoutService } from '../services/checkoutService';
import { OrderService } from '../services/orderService';
import { ApiError } from '../errors/ApiError';


const INTERNATIONAL_SHIPPING_RATE: ShippingRate = {
  serviceType: 'INTERNATIONAL',
  displayName: 'International Shipping',
  carrier: null,
  carrierServiceName: null,
  listPrice: 0,
  accountPrice: 0,
  chargePrice: 0,
  flatRate: true,
  estimatedPickup: null,
  estimatedDeliveryDate: null,
  estimatedDeliveryTime: null,
  estimatedDeliveryText: null,
};

const FREIGHT_PENDING_RATE: ShippingRate = {
  serviceType: 'FREIGHT_PENDING',
  displayName: 'LTL Freight (To Be Arranged)',
  carrier: null,
  carrierServiceName: null,
  listPrice: 0,
  accountPrice: 0,
  chargePrice: 0,
  flatRate: false,
  estimatedPickup: null,
  estimatedDeliveryDate: null,
  estimatedDeliveryTime: null,
  estimatedDeliveryText: null,
};

export function Checkout() {
  const navigate = useNavigate();
  const { state, dispatch } = useShopping();
  const { checkoutData, setCheckoutData, loading: checkoutLoading, error: checkoutError } = useCheckout();
  const { user, refreshPromoCodes } = useUser();
  const { isGuest } = useGuestMode();
  const { clientData } = useClientDataContext();
  const [sdiErrors, setSdiErrors] = useState<Partial<Record<keyof SpecialDeliveryFormData, string>>>({});
  const defaultECheck: ECheckFormData = {
    achFirstName: '',
    achLastName: '',
    achRoutingNumber: '',
    achAccountNumber: '',
    achAccountConfirm: '',
    achAccountType: 'C',
  };

  const [formData, setFormData] = useState<CheckoutFormData>({
    useNewAddress: false,
    billingAddress: {
      sameAsShipping: true,
      streetline1: '',
      streetline2: '',
      city: '',
      state: '',
      postalcode: '',
      phone: '',
    },
    creditCard: {
      cardholderFirstName: '',
      cardholderLastName: '',
    },
    eCheck: defaultECheck,
  });
  const [dynamicShippingRates, setDynamicShippingRates] = useState<ShippingRate[] | null>(null);
  const [ratesDiagnostic, setRatesDiagnostic] = useState<ShippingRatesDiagnosticData | null>(null);
  const [shippingRatesLoading, setShippingRatesLoading] = useState(false);
  const [shippingRatesError, setShippingRatesError] = useState<string | null>(null);
  const [freightUnavailableWarning, setFreightUnavailableWarning] = useState<string | null>(null);
  const [freightNotification, setFreightNotification] = useState(false);
  const [addressValidationWarnings, setAddressValidationWarnings] = useState<string[]>([]);
  const [addressCandidates, setAddressCandidates] = useState<AddressCandidate[] | null>(null);
  const [pendingAddressRequest, setPendingAddressRequest] = useState<{
    address: ShippingLocation | Partial<ShippingLocation>;
    isNewAddress: boolean;
  } | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [highlightField, setHighlightField] = useState<'recipientName' | 'recipientPhone' | null>(null);

  // Set useNewAddress to true for guest users once guest mode is determined
  useEffect(() => {
    if (isGuest) {
      setFormData((prev) => ({ ...prev, useNewAddress: true }));
    }
  }, [isGuest]);

  // Fetch shipping rates for a given address
  const fetchShippingRates = useCallback(async (
    address: ShippingLocation | Partial<ShippingLocation>,
    isNewAddress: boolean,
    skipValidation = false,
    preserveRate: ShippingRate | null = null,
  ) => {
    const generation = ++shippingRequestGenerationRef.current;

    // International shipping is handled manually by warehouse staff at no charge
    if (address.countrycode === 'ZZ') {
      setShippingRatesLoading(false);
      setShippingRatesError(null);
      setFreightUnavailableWarning(null);
      setFreightNotification(false);
      setDynamicShippingRates([INTERNATIONAL_SHIPPING_RATE]);
      setFormData((prev) => ({ ...prev, shippingRate: INTERNATIONAL_SHIPPING_RATE }));
      setCheckoutData?.((prev) => prev ? {
        ...prev,
        shippingRate: null,
        totals: { ...prev.totals, shipping: 0, tax: 0, taxRate: 0 },
        discounts: { ...prev.discounts, shipping: 0, tax: 0, taxRate: 0 },
        netTotals: { ...prev.netTotals, shipping: 0, tax: 0, taxRate: 0 },
      } : prev);
      return;
    }

    // Convert address to request format
    const request = isNewAddress
      ? ShippingService.newAddressToRequest(address)
      : ShippingService.locationToRequest(address as ShippingLocation);

    // If new address is incomplete, don't fetch yet — but clear any stale warning
    if (!request) {
      setFreightUnavailableWarning(null);
      setFreightNotification(false);
      return;
    }

    setShippingRatesLoading(true);
    setShippingRatesError(null);
    setFreightUnavailableWarning(null);
    setFreightNotification(false);
    setAddressValidationWarnings([]);
    setAddressCandidates(null);
    setPendingAddressRequest(null);

    try {
      const result = await ShippingService.getShippingRates(request, skipValidation);

      // Discard if a newer request (e.g. switching to ZZ) has since superseded this one
      if (generation !== shippingRequestGenerationRef.current) return;

      // Check if address validation failed
      if (!result.addressValidated && result.validationWarnings.length > 0) {
        const candidates = result.addressCandidates?.length ? result.addressCandidates : null;
        setAddressCandidates(candidates);
        // Only show the text warning banner when there are no candidates to show in the modal
        setAddressValidationWarnings(candidates ? [] : result.validationWarnings);
        setPendingAddressRequest({ address, isNewAddress });
        setDynamicShippingRates(null);

        if (user?.globalAdmin) {
          const streetlines = [address.address1, address.address2]
            .filter((s): s is string => !!s);
          setRatesDiagnostic(prev => ({
            request: {
              shipTo: {
                streetlines,
                city: address.city ?? null,
                state: address.state ?? null,
                postalcode: (address as ShippingLocation).zip
                  || (address as Partial<ShippingLocation>).postalcode
                  || null,
                countrycode: address.countrycode ?? null,
                residential: address.residential ?? false,
              },
              shipFrom: result.diagnostic?.shipFrom ?? prev?.request?.shipFrom ?? null,
              packages: [],
            },
            addressValidated: false,
            validationWarnings: result.validationWarnings,
            rates: [],
            carrierResponses: null,
            clientCarrierConfig: result.diagnostic?.clientCarrierConfig ?? null,
            validationResponses: result.diagnostic?.validationResponses ?? null,
          }));
        }

        return;
      }

      // When the carrier corrected the address (e.g. "Penn St" → "Pennsylvania Ave"),
      // update the form so the user sees what was actually used.
      if (isNewAddress && result.correctedAddress) {
        const c = result.correctedAddress;
        setFormData(prev => ({
          ...prev,
          newAddress: {
            ...prev.newAddress,
            address1: c.streetlines[0] ?? prev.newAddress?.address1 ?? '',
            address2: c.streetlines[1] || undefined,
            city: c.city ?? prev.newAddress?.city ?? '',
            state: c.state ?? prev.newAddress?.state ?? '',
            zip: c.postalcode ?? prev.newAddress?.zip ?? '',
            postalcode: c.postalcode ?? prev.newAddress?.postalcode ?? '',
            countrycode: c.countrycode ?? prev.newAddress?.countrycode ?? 'US',
          },
        }));
      }

      if (user?.globalAdmin) {
        const streetlines = [address.address1, address.address2]
          .filter((s): s is string => !!s);
        const shipTo = {
          streetlines,
          city: address.city ?? null,
          state: address.state ?? null,
          postalcode: (address as ShippingLocation).zip
            || (address as Partial<ShippingLocation>).postalcode
            || null,
          countrycode: address.countrycode ?? null,
          residential: address.residential ?? false,
        };
        const uniquePackages = result.rates
          .flatMap(r => r.packages ?? [])
          .filter((pkg, i, arr) =>
            arr.findIndex(p => JSON.stringify(p) === JSON.stringify(pkg)) === i
          );
        setRatesDiagnostic(prev => ({
          request: {
            shipTo,
            shipFrom: result.diagnostic?.shipFrom ?? prev?.request?.shipFrom ?? null,
            packages: uniquePackages,
          },
          addressValidated: result.addressValidated,
          validationWarnings: result.validationWarnings,
          rates: result.rates,
          carrierResponses: result.diagnostic?.carrierResponses ?? null,
          clientCarrierConfig: result.diagnostic?.clientCarrierConfig ?? null,
          validationResponses: result.diagnostic?.validationResponses ?? null,
        }));
      }

      // LTL freight cart — skip rate selection entirely and show notification.
      // Warehouse staff will contact the customer to arrange freight; all items ride together.
      if (result.hasFreight) {
        setFreightNotification(true);
        setDynamicShippingRates([]);
        setFormData((prev) => ({ ...prev, shippingRate: FREIGHT_PENDING_RATE }));
        if (generation === shippingRequestGenerationRef.current) {
          try {
            const locationId = !isNewAddress ? (address as ShippingLocation).id ?? null : null;
            await CheckoutService.applyShippingRate(
              locationId,
              FREIGHT_PENDING_RATE.serviceType ?? null,
              FREIGHT_PENDING_RATE.chargePrice ?? null,
              null,
              null,
              null,
              null,
            );
          } catch {
            // non-fatal: rate persisted on next address change
          }
        }
        return;
      }

      const freightUnavailable = result.rates.find(r => r.serviceType === 'FREIGHT_UNAVAILABLE');
      const nonFreightRates = result.rates.filter(r => r.serviceType !== 'FREIGHT_UNAVAILABLE');

      // FREIGHT_UNAVAILABLE is never a selectable option. When it's the only result,
      // surface it as a blocking error. When it accompanies real rates, show a
      // non-blocking warning and let the user pick from the available options.
      if (freightUnavailable && nonFreightRates.length === 0) {
        setShippingRatesError(freightUnavailable.displayName ?? 'Freight shipping is required for this order. Please contact your administrator.');
        setDynamicShippingRates([]);
        setFormData((prev) => ({ ...prev, shippingRate: undefined }));
        setCheckoutData?.((prev) => prev ? {
          ...prev,
          shippingRate: null,
          totals: { ...prev.totals, shipping: 0, tax: 0, taxRate: 0 },
          discounts: { ...prev.discounts, shipping: 0, tax: 0, taxRate: 0 },
          netTotals: { ...prev.netTotals, shipping: 0, tax: 0, taxRate: 0 },
        } : prev);
        return;
      }

      if (freightUnavailable) {
        setFreightUnavailableWarning('Freight shipping is not available for this address. Please select an alternative below.');
      }

      setDynamicShippingRates(nonFreightRates);

      // On initial load, preserve the saved rate if it's still among the valid options.
      // For user-driven address changes, always fall back to the first available rate.
      const savedRate = preserveRate
        ? nonFreightRates.find(r => r.serviceType === preserveRate.serviceType) ?? null
        : null;
      const firstRate = savedRate ?? nonFreightRates[0] ?? null;
      if (firstRate) {
        setFormData((prev) => ({ ...prev, shippingRate: firstRate }));
      }

      // If no rates returned, show an appropriate error and clear any stale selection
      if (result.rates.length === 0) {
        setShippingRatesError('No shipping options available. Please ensure your cart has items.');
        setFormData((prev) => ({ ...prev, shippingRate: undefined }));
        setCheckoutData?.((prev) => prev ? {
          ...prev,
          shippingRate: null,
          totals: { ...prev.totals, shipping: 0, tax: 0, taxRate: 0 },
          discounts: { ...prev.discounts, shipping: 0, tax: 0, taxRate: 0 },
          netTotals: { ...prev.netTotals, shipping: 0, tax: 0, taxRate: 0 },
        } : prev);
      }

      // Persist the auto-selected rate (and location for saved addresses) to the cart so
      // the backend can include shipping in order totals and invoke AvaTax for tax.
      // For new addresses the inline address was already saved by the rates endpoint.
      // Returns fresh checkout data with correct netTotals (including tax).
      if (firstRate && generation === shippingRequestGenerationRef.current) {
        try {
          const locationId = !isNewAddress ? (address as ShippingLocation).id ?? null : null;
          const fresh = await CheckoutService.applyShippingRate(
            locationId,
            firstRate.serviceType ?? null,
            firstRate.chargePrice ?? null,
            firstRate.shipmentPrices ?? null,
            firstRate.carrier ?? null,
            firstRate.carrierServiceCode ?? null,
            firstRate.carrierOptions ?? null,
          );
          if (generation !== shippingRequestGenerationRef.current) return;
          setCheckoutData?.((prev) => prev ? {
            ...prev,
            totals: fresh.totals,
            discounts: fresh.discounts,
            netTotals: fresh.netTotals,
          } : prev);
        } catch (err) {
          // non-fatal: tax display may be stale but order will compute correctly at submission
          console.error('applyShippingRate failed (non-fatal):', err);
        }
      }
    } catch {
      if (generation !== shippingRequestGenerationRef.current) return;
      setShippingRatesError('Unable to load shipping rates. Please check your address.');
      setDynamicShippingRates(null);
    } finally {
      if (generation === shippingRequestGenerationRef.current) {
        setShippingRatesLoading(false);
      }
    }
  }, [user, setCheckoutData]);

  // Continue with unvalidated address
  const handleContinueWithUnvalidatedAddress = useCallback(() => {
    setAddressCandidates(null);
    if (pendingAddressRequest) {
      fetchShippingRates(
        pendingAddressRequest.address,
        pendingAddressRequest.isNewAddress,
        true // skip validation
      );
    }
  }, [pendingAddressRequest, fetchShippingRates]);

  // Use a suggested candidate address returned by the carrier
  const handleSelectCandidate = useCallback((candidate: AddressCandidate) => {
    setAddressCandidates(null);
    const candidateAddress: Partial<ShippingLocation> = {
      address1: candidate.streetlines[0] ?? '',
      address2: candidate.streetlines[1] ?? undefined,
      city: candidate.city ?? '',
      state: candidate.state ?? '',
      zip: candidate.postalcode ?? '',
      postalcode: candidate.postalcode ?? '',
      countrycode: candidate.countrycode ?? 'US',
      residential: true,
    };
    setFormData(prev => ({ ...prev, newAddress: candidateAddress }));
    fetchShippingRates(candidateAddress, true, true); // isNewAddress=true, skipValidation=true
  }, [fetchShippingRates]);

  // Handle saved address selection
  const handleSelectSavedAddress = useCallback((location: ShippingLocation) => {
    setFormData((prev) => ({ ...prev, shippingLocation: location, shippingRate: undefined }));
    fetchShippingRates(location, false);
  }, [fetchShippingRates]);

  // Track whether we've already auto-fetched rates on initial load (saved address flow)
  const hasInitializedRates = useRef(false);
  const shippingRequestGenerationRef = useRef(0);

  // On first load, if checkoutData has a saved shipping location but no cached rates,
  // auto-fetch rates so the shipping method section can render.
  // When checkoutData.shippingRates is already populated we use those directly via the
  // `shippingRates` fallback below, so no fetch is needed.
  useEffect(() => {
    if (hasInitializedRates.current) return;
    if (checkoutData?.allVirtual) return;
    if (!checkoutData?.shippingLocation) return;
    const hasCachedRates = checkoutData.shippingRates && Object.keys(checkoutData.shippingRates).length > 0;
    if (hasCachedRates) return;
    hasInitializedRates.current = true;
    fetchShippingRates(checkoutData.shippingLocation, false, false, checkoutData.shippingRate ?? null);
  }, [checkoutData, fetchShippingRates]);

  // Apply the same FREIGHT_UNAVAILABLE handling to cached rates that fetchShippingRates
  // applies to live results — cached rates bypass the fetch path entirely.
  useEffect(() => {
    if (dynamicShippingRates !== null) return; // fetch path handles its own warning
    const cached: ShippingRate[] = checkoutData?.shippingRates ? Object.values(checkoutData.shippingRates) : [];
    const freightEntry = cached.find(r => r.serviceType === 'FREIGHT_UNAVAILABLE');
    if (!freightEntry) return;
    const otherRates = cached.filter(r => r.serviceType !== 'FREIGHT_UNAVAILABLE');
    if (otherRates.length === 0) {
      setShippingRatesError(freightEntry.displayName ?? 'Freight shipping is required for this order. Please contact your administrator.');
    } else {
      setFreightUnavailableWarning('Freight shipping is not available for this address. Please select an alternative below.');
    }
  }, [checkoutData?.shippingRates, dynamicShippingRates]);

  // Track whether we've already auto-fetched rates for the current new address
  const hasAutoFetchedRates = useRef(false);
  // Track the country code of the new-address form so we can detect a domestic <-> international
  // switch (e.g. New Address -> Other (International)) that should invalidate the selected rate.
  const previousNewAddressCountryCodeRef = useRef<string | undefined>(undefined);

  // Clear the previously selected shipping rate — used both when switching between
  // Saved/New address modes and when the new address's country changes (domestic <-> international),
  // since a rate quoted for one address/country is not valid for another.
  const resetSelectedShippingRate = useCallback(() => {
    ++shippingRequestGenerationRef.current;
    setShippingRatesLoading(false);
    setDynamicShippingRates(null);
    setShippingRatesError(null);
    setFreightUnavailableWarning(null);
    setFreightNotification(false);
    setAddressValidationWarnings([]);
    setAddressCandidates(null);
    setPendingAddressRequest(null);
    hasAutoFetchedRates.current = false;
    // Clear the stale shipping method/cost/tax cached from the previous address so
    // the UI doesn't keep showing rates and tax that no longer apply until new ones load.
    setCheckoutData?.((prev) => prev ? {
      ...prev,
      shippingRate: null,
      shippingRates: {},
      totals: { ...prev.totals, shipping: 0, tax: 0, taxRate: 0 },
      discounts: { ...prev.discounts, shipping: 0, tax: 0, taxRate: 0 },
      netTotals: { ...prev.netTotals, shipping: 0, tax: 0, taxRate: 0 },
    } : prev);
  }, [setCheckoutData]);

  // Handle new address field changes — auto-fetch once when address is complete.
  // For domestic: triggers when zip reaches 5+ chars.
  // For Other International (ZZ): triggers when countryName is filled.
  const handleNewAddressChange = useCallback((address: Partial<ShippingLocation>) => {
    // address.countrycode stays undefined until the country <select> is touched — the
    // "United States" shown until then is just this same UI fallback, not stored state —
    // so compare against that fallback rather than the raw (often-undefined) value.
    const previousCountryCode = previousNewAddressCountryCodeRef.current;
    const nextCountryCode = address.countrycode ?? 'US';
    const countryCodeChanged = previousCountryCode !== undefined && previousCountryCode !== nextCountryCode;
    previousNewAddressCountryCodeRef.current = nextCountryCode;

    setFormData((prev) => ({
      ...prev,
      newAddress: address,
      shippingRate: countryCodeChanged ? undefined : prev.shippingRate,
    }));

    if (countryCodeChanged) {
      resetSelectedShippingRate();
    }

    const isOtherIntl = address.countrycode === 'ZZ';
    const isComplete = isOtherIntl
      ? !!(address.address1 && address.city && address.state && address.countryName)
      : !!(address.address1 && address.city && address.state && address.zip && address.zip.length >= 5);

    if (!hasAutoFetchedRates.current && isComplete) {
      hasAutoFetchedRates.current = true;
      fetchShippingRates(address, true);
    }
  }, [fetchShippingRates, resetSelectedShippingRate]);

  // Manual re-fetch for the button shown after initial auto-fetch
  const handleFetchRates = useCallback(() => {
    if (formData.newAddress) {
      fetchShippingRates(formData.newAddress, true);
    }
  }, [formData.newAddress, fetchShippingRates]);

  // All display values use gross (pre-promo) amounts so the user can see what they're paying
  // for each line. The promo discount is shown as a single line equal to discounts.total so
  // the math is self-consistent: subtotal + shipping + tax - promoDiscount = total (= 0 when
  // fully covered). Handling and pick-pack are folded into subtotal so no hidden fees skew the math.
  const subtotal = checkoutData?.totals.subtotal ?? 0;
  const handlingFee = checkoutData?.totals.handlingFee ?? 0;
  const pickPackFee = checkoutData?.totals.pickPackFee ?? 0;
  const shippingCost = checkoutData?.totals.shipping ?? 0;
  const tax = checkoutData?.totals.tax ?? 0;
  // netTotal uses netTotals directly (post-promo, accurate after applyPromo saves the rate).
  const netTotal = (checkoutData?.netTotals.subtotal ?? 0)
    + (checkoutData?.netTotals.shipping ?? 0)
    + (checkoutData?.netTotals.tax ?? 0)
    + (checkoutData?.netTotals.handlingFee ?? 0)
    + (checkoutData?.netTotals.pickPackFee ?? 0);
  const total = netTotal;
  const promoDiscount = checkoutData
    ? (checkoutData.discounts.subtotal + checkoutData.discounts.shipping +
       checkoutData.discounts.handlingFee + checkoutData.discounts.tax +
       checkoutData.discounts.pickPackFee)
    : 0;
  const quantityDiscount = checkoutData?.quantityDiscountSavings || 0;
  const configurationWarnings = checkoutData?.configurationWarnings ?? [];
  const promoErrors = checkoutData?.promoErrors ?? [];

  // Destructure data from checkoutData
  const promoCodes = checkoutData?.promoCodes ? Object.values(checkoutData.promoCodes) : [];
  const savedShippingLocations = checkoutData?.savedShippingLocations || [];
  // Use dynamic shipping rates if available, otherwise fall back to checkout data,
  // filtering out FREIGHT_UNAVAILABLE so it never appears as a selectable option.
  const rawCachedRates: ShippingRate[] = checkoutData?.shippingRates ? Object.values(checkoutData.shippingRates) : [];
  const shippingRates = dynamicShippingRates ?? rawCachedRates.filter(r => r.serviceType !== 'FREIGHT_UNAVAILABLE');
  const paymentTypes = checkoutData?.paymentTypes || [];

  const isAdmin = !!(user?.globalAdmin || user?.distributorAdmin || user?.clientAdmin);

  // Only admins can select an accounting code; non-admins see their assigned code as read-only
  const accountingCodes: AccountingCode[] = (isAdmin && user?.client?.accounting_codes)
    ? user.client.accounting_codes.map((code) => ({
        id: code.id,
        name: code.name,
        active: code.status,
      }))
    : [];

  // Personal code shown as read-only for non-admins (admins use the dropdown instead)
  const userAccountingCode = user?.accountingCode?.name ?? undefined;

  // Cart contains only virtual items (e.g. digital products) — no shipping needed
  const isVirtualOnlyCart = !!checkoutData?.allVirtual;

  // Determine if a valid shipping address has been provided
  const isOtherInternational = formData.newAddress?.countrycode === 'ZZ';
  const hasValidAddress = formData.useNewAddress
    ? !!(
        formData.newAddress?.address1 &&
        formData.newAddress?.city &&
        formData.newAddress?.state &&
        (isOtherInternational ? formData.newAddress?.countryName : formData.newAddress?.zip)
      )
    : !!formData.shippingLocation;

  // Set default shipping location, promo, and recipient info on load
  useEffect(() => {
    if (checkoutData?.shippingLocation) {
      setFormData((prev) => ({ ...prev, shippingLocation: prev.shippingLocation ?? checkoutData.shippingLocation }));
    }
    if (checkoutData?.shippingRate) {
      setFormData((prev) => ({ ...prev, shippingRate: prev.shippingRate ?? checkoutData.shippingRate }));
    }
    if (checkoutData?.shippingName) {
      setFormData((prev) => ({ ...prev, recipientName: prev.recipientName ?? checkoutData.shippingName ?? undefined }));
    }
    if (checkoutData?.shippingPhone) {
      setFormData((prev) => ({ ...prev, recipientPhone: prev.recipientPhone ?? checkoutData.shippingPhone ?? undefined }));
    }
    // Restore the saved promo from the cart on first load only (undefined = never set by user).
    // checkoutData.promoCode is the promo code string from the API; resolve it to a full
    // PromoCode object by matching against the user's available promoCodes.
    setFormData((prev) => {
      if (prev.promoCode !== undefined) return prev;
      if (!checkoutData) return prev; // not loaded yet — keep undefined so we retry when data arrives
      const savedCode = checkoutData.promoCode as unknown as string | null;
      const matched = savedCode
        ? Object.values(checkoutData.promoCodes ?? {}).find(p => p.promo === savedCode) ?? null
        : null;
      return { ...prev, promoCode: matched ?? undefined };
    });
    // Pre-populate the accounting code dropdown for admin users: prefer the cart's saved
    // code, then fall back to the user's assigned code matched by ID from the available list.
    setFormData((prev) => {
      if (prev.accountingCode !== undefined || !isAdmin) return prev;
      if (checkoutData?.accountingCode?.active) return { ...prev, accountingCode: checkoutData.accountingCode };
      if (user?.accountingCode) {
        const match = accountingCodes.find(c => c.id === user.accountingCode!.id);
        if (match) return { ...prev, accountingCode: match };
      }
      return prev;
    });
  }, [checkoutData, isAdmin]);

  useEffect(() => {
    if (pendingSubmit && formData.paymentToken) {
      setPendingSubmit(false);
      setSubmissionError(null);
      // Re-enter handleSubmit so all remaining validations (phone, checkout fields, etc.) run.
      // The token is now set, so the credit card block is skipped and execution continues normally.
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSubmit/submitOrder intentionally excluded; they capture current formData at call time and including them would cause re-fires
  }, [formData.paymentToken, pendingSubmit]);

  const handleSubmit = async () => {
    setSubmissionError(null);

    // Validate required fields (skip when netTotal is $0 — no payment needed)
    if (!formData.paymentType && netTotal > 0) {
      setSubmissionError('Please select a payment method');
      return;
    }

    // For credit card payments with a non-zero net total, ensure we have a token
    if (formData.paymentType === 'CREDIT' && netTotal > 0) {
      if (!formData.paymentToken) {
        // Validate credit card form fields before tokenizing
        const ccForm = document.getElementById('credit-card-form') as HTMLFormElement | null;
        if (ccForm && !ccForm.reportValidity()) {
          return;
        }

        // Trigger tokenization first
        const tokenizeCard = (window as unknown as Record<string, unknown>).tokenizeCreditCard as (() => void) | undefined;
        if (tokenizeCard) {
          tokenizeCard();
          setPendingSubmit(true);
          setSubmissionError('Processing payment information...');
          setTimeout(() => {
            setPendingSubmit((pending) => {
              if (pending) {
                setSubmissionError('Payment processing timed out. Please try again.');
              }
              return false;
            });
          }, 30000);
          return;
        }
        setSubmissionError('Please enter your credit card information');
        return;
      }
    }

    // For eCheck payments, validate required fields and account number confirmation
    if (formData.paymentType === 'ECHECK' && netTotal > 0) {
      const ach = formData.eCheck;
      if (!ach?.achFirstName || !ach?.achLastName || !ach?.achRoutingNumber || !ach?.achAccountNumber || !ach?.achAccountConfirm) {
        setSubmissionError('Please fill in all eCheck fields');
        return;
      }
      if (ach.achRoutingNumber.length !== 9) {
        setSubmissionError('Routing number must be exactly 9 digits');
        return;
      }
      if (ach.achAccountNumber.length < 4) {
        setSubmissionError('Account number must be at least 4 digits');
        return;
      }
      if (ach.achAccountNumber !== ach.achAccountConfirm) {
        setSubmissionError('Account numbers do not match');
        return;
      }
    }

    // For guests, require email
    if (isGuest && !formData.guestEmail) {
      setSubmissionError('Please enter your email address');
      return;
    }

    // Validate recipient name and phone
    if (!formData.recipientName?.trim()) {
      setHighlightField('recipientName');
      setSubmissionError('Please enter the recipient name');
      return;
    }
    const isInternational = !!clientData?.internationalShipping;
    const phoneDigits = formData.recipientPhone?.replace(/\D/g, '') ?? '';
    const phoneInvalid = isInternational
      ? !formData.recipientPhone?.trim()
      : phoneDigits.length !== 10;
    if (phoneInvalid) {
      setHighlightField('recipientPhone');
      setSubmissionError(
        isInternational
          ? 'Please enter a phone number'
          : 'Please enter a valid 10-digit phone number',
      );
      return;
    }

    // Validate required checkout fields
    const checkoutFields = checkoutData?.checkoutFields ?? [];
    for (const field of checkoutFields) {
      if (field.required && !formData.checkoutFieldValues?.[field.name]) {
        setSubmissionError(`Please fill in the required field: ${field.label}`);
        return;
      }
    }

    // Validate required initials field
    const initialsField = checkoutData?.initialsField;
    if (initialsField?.required && !formData.checkoutFieldValues?.[initialsField.name]) {
      setSubmissionError(`Please enter your initials to acknowledge: ${initialsField.label}`);
      return;
    }

    // Validate Special Delivery Instructions when enabled
    if (clientData?.specialDeliveryInstructions && formData.shippingRate) {
      const sdi = formData.specialDelivery;
      const newSdiErrors: Partial<Record<keyof SpecialDeliveryFormData, string>> = {};
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (!sdi?.deliveryStartDate) {
        newSdiErrors.deliveryStartDate = 'Delivery start date is required';
      } else if (new Date(sdi.deliveryStartDate) < today) {
        newSdiErrors.deliveryStartDate = 'Delivery start date cannot be in the past';
      } else if (clientData.specialDeliveryMinDayToEvent != null) {
        const msPerDay = 1000 * 60 * 60 * 24;
        const daysUntilDelivery = Math.round(
          (new Date(sdi.deliveryStartDate).getTime() - today.getTime()) / msPerDay
        );
        if (daysUntilDelivery > clientData.specialDeliveryMinDayToEvent) {
          newSdiErrors.deliveryStartDate = `Orders must be placed within ${clientData.specialDeliveryMinDayToEvent} days of delivery start date`;
        }
      }

      if (!sdi?.deliveryEndDate) {
        newSdiErrors.deliveryEndDate = 'Delivery end date is required';
      } else if (sdi.deliveryStartDate && new Date(sdi.deliveryEndDate) < new Date(sdi.deliveryStartDate)) {
        newSdiErrors.deliveryEndDate = 'Delivery end date cannot be before delivery start date';
      }

      if (sdi?.eventStartDate || sdi?.eventEndDate) {
        if (sdi.eventStartDate && sdi.deliveryEndDate && new Date(sdi.eventStartDate) < new Date(sdi.deliveryEndDate)) {
          newSdiErrors.eventStartDate = 'Event start date must be on or after delivery end date';
        }
        if (sdi.eventEndDate && sdi.eventStartDate && new Date(sdi.eventEndDate) < new Date(sdi.eventStartDate)) {
          newSdiErrors.eventEndDate = 'Event end date cannot be before event start date';
        }
      }

      if (Object.keys(newSdiErrors).length > 0) {
        setSdiErrors(newSdiErrors);
        return;
      }
    }

    setSdiErrors({});
    await submitOrder();
  };

  const submitOrder = async () => {
    setIsSubmitting(true);

    try {
      const result = await OrderService.submitOrder({
        guestEmail: formData.guestEmail,
        recipientName: formData.recipientName,
        recipientPhone: formData.recipientPhone,
        savePhone: formData.savePhone,
        paymentType: netTotal > 0 ? (formData.paymentType ?? paymentTypes[0]) : formData.paymentType,
        paymentToken: formData.paymentToken,
        cardholderFirstName: formData.creditCard?.cardholderFirstName,
        cardholderLastName: formData.creditCard?.cardholderLastName,
        billingAddress: formData.billingAddress,
        achFirstName: formData.eCheck?.achFirstName,
        achLastName: formData.eCheck?.achLastName,
        achRoutingNumber: formData.eCheck?.achRoutingNumber,
        achAccountNumber: formData.eCheck?.achAccountNumber,
        achAccountType: formData.eCheck?.achAccountType,
        shippingAddress: formData.useNewAddress ? {
          location: formData.newAddress?.location || undefined,
          address1: formData.newAddress?.address1 || '',
          address2: formData.newAddress?.address2 || undefined,
          city: formData.newAddress?.city || '',
          state: formData.newAddress?.state || '',
          postalcode: formData.newAddress?.zip || undefined,
          countrycode: formData.newAddress?.countrycode || 'US',
          countryName: formData.newAddress?.countryName || undefined,
          residential: true,
        } : undefined,
        comments: formData.comments,
        shippingLocationId: formData.useNewAddress ? undefined : formData.shippingLocation?.id,
        shippingServiceType: formData.shippingRate?.serviceType,
        shippingChargePrice: formData.shippingRate?.chargePrice,
        specialDeliveryInstructions: formData.specialDelivery,
        checkoutFieldValues: formData.checkoutFieldValues,
        accountingCodeId: formData.accountingCode?.id,
      });

      const cartSnapshot = state.cart;
      // Clear cart state (backend already deleted the cart)
      dispatch({ type: 'SET_CART', payload: [] });
      // Refresh promo balances so the header and next checkout both show the updated amount.
      refreshPromoCodes?.();
      // Navigate to order confirmation page
      navigate(`/order-confirmation/${result.masterOrderNo}`, { state: { orders: result.orders, cart: cartSnapshot } });
    } catch (error) {
      console.error('Order submission failed:', error);
      if (error instanceof ApiError) {
        setSubmissionError(error.message);
      } else if (error instanceof Error) {
        setSubmissionError(error.message);
      } else {
        setSubmissionError('Failed to submit order. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show skeleton while checkout data is being fetched — must come before the cart
  // check so we don't flash "Your cart is empty" while cart state is still loading.
  if (checkoutLoading) {
    return (
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 animate-pulse">
          {/* Header */}
          <div className="mb-8">
            <div className="h-4 w-36 bg-gray-200 rounded mb-4" />
            <div className="h-8 w-40 bg-gray-200 rounded" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Left column — form sections */}
            <div className="lg:col-span-2 space-y-6">
              {/* Promo / accounting row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2].map(i => (
                  <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                    <div className="h-4 w-32 bg-gray-200 rounded mb-4" />
                    <div className="h-10 bg-gray-100 rounded" />
                  </div>
                ))}
              </div>

              {/* Shipping address */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                <div className="h-5 w-40 bg-gray-200 rounded mb-5" />
                <div className="space-y-3">
                  <div className="h-10 bg-gray-100 rounded" />
                  <div className="grid grid-cols-2 gap-3">
                    <div className="h-10 bg-gray-100 rounded" />
                    <div className="h-10 bg-gray-100 rounded" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="h-10 bg-gray-100 rounded" />
                    <div className="h-10 bg-gray-100 rounded" />
                    <div className="h-10 bg-gray-100 rounded" />
                  </div>
                </div>
              </div>

              {/* Shipping method */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                <div className="h-5 w-36 bg-gray-200 rounded mb-5" />
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg">
                      <div className="w-4 h-4 rounded-full bg-gray-200 flex-shrink-0" />
                      <div className="flex-1 h-4 bg-gray-200 rounded" />
                      <div className="h-4 w-16 bg-gray-200 rounded" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment method */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                <div className="h-5 w-36 bg-gray-200 rounded mb-5" />
                <div className="grid grid-cols-2 gap-3">
                  {[1, 2].map(i => (
                    <div key={i} className="h-12 bg-gray-100 rounded-lg" />
                  ))}
                </div>
              </div>
            </div>

            {/* Right column — order summary */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="h-5 w-32 bg-gray-200 rounded mb-5" />
              <div className="space-y-3 mb-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex gap-3">
                    <div className="w-14 h-14 bg-gray-200 rounded flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-100 pt-4 space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex justify-between">
                    <div className="h-3 w-20 bg-gray-200 rounded" />
                    <div className="h-3 w-14 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
              <div className="mt-5 h-11 bg-gray-200 rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state.cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your cart is empty</h2>
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  // Show error state if checkout data failed to load
  if (checkoutError || !checkoutData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Failed to load checkout data</h2>
          <p className="text-gray-600 mb-4">{checkoutError}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Continue Shopping
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
        </div>

        {/* Configuration warnings - products with missing dimensions */}
        {configurationWarnings.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-semibold text-red-800 mb-1">Your order cannot be submitted:</p>
                {configurationWarnings.map((warning, index) => (
                  <p key={index} className="text-red-700">{warning}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Promo code errors - promo_code_only items without a valid promo */}
        {promoErrors.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                {promoErrors.map((error, index) => (
                  <p key={index} className="text-red-800 font-medium">{error}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {!checkoutData.initialsField && checkoutData.checkoutMessage && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div
                  className="text-sm text-gray-700 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: checkoutData.checkoutMessage }}
                />
              </div>
            )}

            {/* Guest Email Section - Only for guests */}
            {isGuest && (
              <GuestEmailSection
                email={formData.guestEmail}
                onEmailChange={(guestEmail) =>
                  setFormData({ ...formData, guestEmail })
                }
              />
            )}

            {/* Promo Code and Accounting Code - Only for logged-in users */}
            {!isGuest && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <PromoCodeSection
                  promoCodes={promoCodes}
                  selectedPromoCode={formData.promoCode}
                  onPromoCodeChange={async (promoCode) => {
                    setFormData({ ...formData, promoCode });
                    try {
                      const fresh = await CheckoutService.applyPromo(
                        promoCode?.id ?? null,
                        formData.shippingRate?.chargePrice ?? null,
                        formData.shippingLocation?.id ?? null,
                        formData.shippingRate?.serviceType ?? null,
                      );
                      setCheckoutData?.({ ...checkoutData!, ...fresh, savedShippingLocations: checkoutData!.savedShippingLocations, promoCodes: checkoutData!.promoCodes });
                    } catch {
                      // non-fatal: netTotals may be stale but order will still apply the promo
                    }
                  }}
                />
                <AccountingCodeSection
                  accountingCodes={accountingCodes}
                  selectedAccountingCode={formData.accountingCode}
                  onAccountingCodeChange={(accountingCode) =>
                    setFormData({ ...formData, accountingCode })
                  }
                  displayOnlyValue={userAccountingCode}
                />
              </div>
            )}

            {/* Initials acknowledgment - always above other custom fields */}
            {checkoutData.initialsField && (
              <InitialsAcknowledgmentSection
                field={checkoutData.initialsField}
                checkoutMessage={checkoutData.checkoutMessage}
                value={formData.checkoutFieldValues?.[checkoutData.initialsField.name] ?? ''}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    checkoutFieldValues: {
                      ...(formData.checkoutFieldValues ?? {}),
                      [checkoutData.initialsField!.name]: value,
                    },
                  })
                }
              />
            )}

            {/* Order Information - custom checkout fields */}
            {(checkoutData.checkoutFields?.length ?? 0) > 0 && (
              <OrderInformationSection
                fields={checkoutData.checkoutFields!}
                values={formData.checkoutFieldValues ?? {}}
                onChange={(checkoutFieldValues) => setFormData({ ...formData, checkoutFieldValues })}
              />
            )}

            <ShippingAddressSection
              hideAddress={isVirtualOnlyCart}
              savedShippingLocations={savedShippingLocations}
              useNewAddress={formData.useNewAddress}
              selectedShippingLocation={formData.shippingLocation}
              newAddress={formData.newAddress}
              onToggleAddressMode={(useNew) => {
                setFormData({
                  ...formData,
                  useNewAddress: useNew,
                  shippingLocation: useNew ? undefined : formData.shippingLocation,
                  shippingRate: undefined,
                });
                previousNewAddressCountryCodeRef.current = undefined;
                resetSelectedShippingRate();
              }}
              onSelectSavedAddress={handleSelectSavedAddress}
              onNewAddressChange={handleNewAddressChange}
              onFetchRates={handleFetchRates}
              shippingRatesLoading={shippingRatesLoading}
              showFetchRatesButton={hasAutoFetchedRates.current}
              isGuest={isGuest}
              recipientName={formData.recipientName}
              recipientPhone={formData.recipientPhone}
              onRecipientNameChange={(recipientName) => {
                setFormData({ ...formData, recipientName });
                if (highlightField === 'recipientName') setHighlightField(null);
              }}
              onRecipientPhoneChange={(recipientPhone) => {
                setFormData({ ...formData, recipientPhone });
                if (highlightField === 'recipientPhone') setHighlightField(null);
              }}
              highlightField={highlightField}
              savePhone={formData.savePhone}
              onSavePhoneChange={(savePhone) =>
                setFormData({ ...formData, savePhone })
              }
              internationalShipping={!!clientData?.internationalShipping}
            />

            {/* Address validation warning - shown when address could not be validated */}
            {!isVirtualOnlyCart && addressValidationWarnings.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Address Validation</h2>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="flex-1">
                      {addressValidationWarnings.map((warning, index) => (
                        <p key={index} className="text-yellow-800">{warning}</p>
                      ))}
                      <button
                        type="button"
                        onClick={handleContinueWithUnvalidatedAddress}
                        className="mt-3 px-4 py-2 bg-yellow-600 text-white font-medium rounded-lg hover:bg-yellow-700 transition-colors"
                      >
                        Continue with Unvalidated Address
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Shipping rates loading indicator - only shown after address is provided and no validation warnings */}
            {!isVirtualOnlyCart && hasValidAddress && shippingRatesLoading && addressValidationWarnings.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping Method</h2>
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading shipping rates...</span>
                </div>
              </div>
            )}

            {/* Shipping rates error - only shown after address is provided and no validation warnings */}
            {!isVirtualOnlyCart && hasValidAddress && !shippingRatesLoading && shippingRatesError && addressValidationWarnings.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping Method</h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-red-800">{shippingRatesError}</span>
                  </div>
                </div>
              </div>
            )}

            {/* LTL freight notification - shown when cart contains a heavy freight item */}
            {!isVirtualOnlyCart && hasValidAddress && !shippingRatesLoading && freightNotification && addressValidationWarnings.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-amber-800">This shipment may qualify for LTL freight. We will contact you to discuss shipping.</span>
                </div>
              </div>
            )}

            {/* Freight unavailable warning - non-blocking, shown alongside available rates */}
            {!isVirtualOnlyCart && hasValidAddress && !shippingRatesLoading && !shippingRatesError && freightUnavailableWarning && addressValidationWarnings.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-amber-800">{freightUnavailableWarning}</span>
                </div>
              </div>
            )}

            {/* Shipping method selection - only shown when address is provided, validated, and rates are available */}
            {!isVirtualOnlyCart && hasValidAddress && !shippingRatesLoading && !shippingRatesError && addressValidationWarnings.length === 0 && shippingRates.length > 0 && (
              <ShippingMethodSection
                shippingRates={shippingRates}
                selectedRate={formData.shippingRate}
                onRateChange={async (rate) => {
                  setFormData((prev) => ({ ...prev, shippingRate: rate }));
                  try {
                    const locationId = !formData.useNewAddress ? formData.shippingLocation?.id ?? null : null;
                    const fresh = await CheckoutService.applyShippingRate(
                      locationId,
                      rate.serviceType ?? null,
                      rate.chargePrice ?? null,
                      rate.shipmentPrices ?? null,
                      rate.carrier ?? null,
                      rate.carrierServiceCode ?? null,
                      rate.carrierOptions ?? null,
                    );
                    setCheckoutData?.((prev) => prev ? {
                      ...prev,
                      totals: fresh.totals,
                      discounts: fresh.discounts,
                      netTotals: fresh.netTotals,
                    } : prev);
                  } catch (err) {
                    console.error('applyShippingRate failed (non-fatal):', err);
                  }
                }}
              />
            )}

            {clientData?.specialDeliveryInstructions && !!formData.shippingRate && (
              <SpecialDeliverySection
                value={formData.specialDelivery ?? {
                  deliveryStartDate: '',
                  deliveryEndDate: '',
                  eventStartDate: '',
                  eventEndDate: '',
                  shipmentContent: '',
                }}
                onChange={(specialDelivery) => setFormData({ ...formData, specialDelivery })}
                minDayToEvent={clientData.specialDeliveryMinDayToEvent}
                errors={sdiErrors}
              />
            )}

            {(isVirtualOnlyCart || formData.shippingRate) && netTotal > 0 && (
              <PaymentMethodSection
                paymentTypes={paymentTypes}
                selectedPaymentType={formData.paymentType}
                onPaymentTypeChange={(paymentType) =>
                  setFormData({ ...formData, paymentType })
                }
              />
            )}

            {/* Comments - shown for warehouse fulfillment orders */}
            {checkoutData.hasWarehouseItems && (
              <CommentsSection
                value={formData.comments ?? ''}
                onChange={(comments) => setFormData({ ...formData, comments })}
              />
            )}

            {/* Credit Card Section - shown when CREDIT selected and net total > $0 */}
            {formData.paymentType === 'CREDIT' && netTotal > 0 && (
              <>
                <CreditCardSection
                  billingAddress={formData.billingAddress!}
                  creditCard={formData.creditCard!}
                  shippingAddress={formData.useNewAddress ? formData.newAddress : formData.shippingLocation}
                  onBillingAddressChange={(billingAddress) =>
                    setFormData({ ...formData, billingAddress })
                  }
                  onCreditCardChange={(creditCard) =>
                    setFormData({ ...formData, creditCard })
                  }
                  onTokenReceived={(paymentToken) => {
                    setFormData((prev) => ({ ...prev, paymentToken }));
                    setPaymentError(null);
                  }}
                  onError={(err) => {
                    setPaymentError(err);
                    setPendingSubmit(false);
                    setSubmissionError(null);
                  }}
                  totalAmount={total}
                />
              </>
            )}

            {/* eCheck Section - shown when ECHECK selected and net total > $0 */}
            {formData.paymentType === 'ECHECK' && netTotal > 0 && (
              <ECheckSection
                eCheck={formData.eCheck!}
                onChange={(eCheck) => setFormData({ ...formData, eCheck })}
                totalAmount={total}
              />
            )}
          </div>

          <div className="lg:col-span-1 lg:self-stretch">
            <OrderSummary
              cartItems={state.cart}
              subtotal={subtotal}
              quantityDiscount={quantityDiscount}
              promoDiscount={promoDiscount}
              handlingFee={handlingFee}
              pickPackFee={pickPackFee}
              shippingCost={shippingCost}
              tax={tax}
              total={total}
              isVirtualOnlyCart={isVirtualOnlyCart}
              canSubmit={(isVirtualOnlyCart || (hasValidAddress && !!formData.shippingRate && !shippingRatesError && !shippingRatesLoading && addressCandidates === null && addressValidationWarnings.length === 0) || (hasValidAddress && freightNotification && addressCandidates === null && addressValidationWarnings.length === 0)) && (!!formData.paymentType || netTotal <= 0) && !isSubmitting && configurationWarnings.length === 0 && promoErrors.length === 0}
              isSubmitting={isSubmitting}
              paymentError={paymentError}
              submissionError={submissionError}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>

      {user?.globalAdmin && <DiagnosticPanel ratesDiagnostic={ratesDiagnostic} />}

      {addressCandidates && (
        <AddressCandidatesModal
          candidates={addressCandidates}
          onSelect={handleSelectCandidate}
          onContinueUnvalidated={() => {
            setAddressCandidates(null);
            setAddressValidationWarnings(pendingAddressRequest ? ['The address you entered could not be validated.'] : []);
          }}
        />
      )}
    </div>
  );
}
